import { Dispatch, MouseEvent, SetStateAction, useCallback, useEffect, useState } from 'react'
import { RootState } from 'src/store/store'
import { useLocation, useNavigate } from 'react-router-dom'
import ExplorerDetailTemplate from 'src/components/atomic/template/Service/ExplorerDetail'
import { SvgType } from 'src/types/types'
import { useAppDispatch } from 'src/hooks/redux/useAppDispatch'
import { useAppSelector } from 'src/hooks/redux/useAppSelector'
import { Cmdtypes } from 'src/constants/cmdtypes'
import {
  ResetExplorerFileList,
  setExplorerFileInfos,
} from 'src/store/Reducer/socket/ExplorerfileListSlice'
import { setModalItems } from 'src/store/Reducer/common/modalSlice'
import useExplorerDetailEventBus from 'src/hooks/useEventBus/useExplorerDetailEventBus'
import { commonUtils } from 'src/utils/common.utils'
import useMatchHook from 'src/hooks/useMatch.hook'

import {
  apkSvgList,
  audioSvgList,
  documentSvgList,
  imageSvgList,
  videoSvgList,
} from 'src/constants/fileExtension'
import { FileInfosType, RequestDataBody } from 'src/types/websocket'
import { SocketSender } from 'src/api/socket'
import { useTranslation } from 'react-i18next'
import { setExplorerFileListLoading } from 'src/store/Reducer/page/explorerPageSlice'

export type ExplorerDetailDataPropsType = {
  ExtensionSvgHandler: (extension: string) => SvgType
  FolderInnerDispatch: (folderName: string) => void
  curPage: number
  setCurPage: Dispatch<SetStateAction<number>>
  eachCheckboxHandler: (event: MouseEvent<HTMLLabelElement, MouseEvent>, index: number) => void
  paginationHandler: (page: number) => void
  ResultPageList: number[]
  allCheckboxChange: () => void
  backUpRequestHandler: () => void
  fileSizeHandler: (size: any) => string
  fileDataTotalValue: { checkItemsSize: number; allItemsSize: number }
  allCheckValid: boolean
  arrayCheckList: FileInfosType[]
  backupBtnDisabled: boolean
  selectedFileInfoHandler: () => string
  directoryPathHandler: () => string
}

const ExplorerDetailPage = () => {
  const { externalDefaultPath, DownloadsDefaultPath, InternalDefaultPath } = commonUtils
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [curPage, setCurPage] = useState(1)
  const { FileInfos, processFileInfo, Path } = useAppSelector(
    (state: RootState) => state.explorerFileList,
  )
  const { Type, Title } = useLocation()?.state
  const { Connection } = useAppSelector((state: RootState) => state.connection)
  const deviceAuth = useAppSelector((state: RootState) => state.deviceAuth)
  const navigate = useNavigate()
  const [backupBtnDisabled, setBackupBtnDisabled] = useState(false)
  const [shiftKeyFirstIdx, setShiftKeyFirstIdx] = useState<number>()
  const { m_explorer_detail_param } = useMatchHook()

  const limitSize = 314572800

  const ExtensionSvgHandler = (extension: string): SvgType => {
    switch (true) {
      case imageSvgList.includes(extension):
        return 'explorer_image_icon'
      case audioSvgList.includes(extension):
        return 'explorer_audios_icon'
      case videoSvgList.includes(extension):
        return 'explorer_video_icon'
      case documentSvgList.includes(extension):
        return 'explorer_document_icon'
      case apkSvgList.includes(extension):
        return 'explorer_apk_icon'
      default:
        return 'explorer_default_icon'
    }
  }

  const fileSizeHandler = (size: any): string => {

    const sizeUnits: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (size === 0) return '0 Byte'
    const i: number = Math.floor(Math.log(size) / Math.log(1024))
    return (size / Math.pow(1024, i)).toFixed(1) + ' ' + sizeUnits[i]
  }

  const allCheckValid = FileInfos?.every((f: FileInfosType) => f.check)
  const arrayCheckList: FileInfosType[] = FileInfos?.filter((f: FileInfosType) => f.check)

  const fileDataTotalValue = useCallback(() => {
    let checkItemsSize = 0
    let allItemsSize = 0

    arrayCheckList?.forEach((item) => {
      checkItemsSize += item.Size
    })

    FileInfos?.forEach((item) => {
      allItemsSize += item.Size
    })

    return {
      allItemsSize,
      checkItemsSize,
    }
  }, [arrayCheckList, FileInfos])

  //현재 페이지가 포함된 배열
  const ResultPageList = processFileInfo.p_filePageDivisionArray?.filter((element: number[]) =>
    element.includes(curPage),
  )?.[0]

  //내부 폴더 조회
  const FolderInnerDispatch = (folderName: string) => {
    dispatch(
      SocketSender<RequestDataBody.ExplorerFileInnerList>(Cmdtypes.EXPLORER_FOLDER_INNER, {
        Path: `${Path}/${folderName}`,
        Page: 1,
      }),
    )
  }

  const fileListResetDispatch = useCallback(() => {
    dispatch(ResetExplorerFileList())
    dispatch(setExplorerFileListLoading(true))
  }, [dispatch])

  const paginationHandler = (page: number) => {
    fileListResetDispatch()
    switch (m_explorer_detail_param) {
      case 'INTERNAL':
        dispatch(
          SocketSender<RequestDataBody.ExplorerFileInnerList>(Cmdtypes.EXPLORER_FOLDER_INNER, {
            Path: !!Path ? Path : commonUtils.InternalDefaultPath,
            Page: page,
          }),
        )
        break
      case 'EXTERNAL':
        dispatch(
          SocketSender<RequestDataBody.ExplorerFileInnerList>(Cmdtypes.EXPLORER_FOLDER_INNER, {
            Path: !!Path ? Path : commonUtils.externalDefaultPath,
            Page: page,
          }),
        )
        break
      case 'Downloads':
        dispatch(
          SocketSender<RequestDataBody.ExplorerFileInnerList>(Cmdtypes.EXPLORER_FOLDER_INNER, {
            Path: !!Path ? Path : commonUtils.DownloadsDefaultPath,
            Page: page,
          }),
        )
        break
      default:
        const data = { Type: Type, Page: page }
        dispatch(SocketSender<RequestDataBody.ExplorerFileList>(Cmdtypes.EXPLORER_FILE_LIST, data))
        break
    }
    setCurPage(page)
  }

  //체크박스 상태관리(each)
  const eachCheckboxHandler = (event: MouseEvent<HTMLLabelElement, MouseEvent>, index: number) => {
    const { checkItemsSize } = fileDataTotalValue()
    const updatedFileInfos = [...FileInfos]
    const pressShiftKey = event.shiftKey
    let IdxArray: number[] = []
    let sizeSum = 0

    if (pressShiftKey) {
      !shiftKeyFirstIdx && setShiftKeyFirstIdx(index)
      const minNum = Math.min(shiftKeyFirstIdx as number, index)
      const maxNum = Math.max(shiftKeyFirstIdx as number, index)
      const NumBetweenMinMax = Array.from(
        { length: maxNum - minNum + 1 },
        (_, index) => minNum + index,
      )

      IdxArray = !isNaN(minNum) && !isNaN(maxNum) ? NumBetweenMinMax : [index]

      IdxArray.forEach((num, idx) => {
        const { Size, check } = updatedFileInfos[num]
        const IdxArrayLength = IdxArray?.length

        if (check) return

        const LimitSizeCondition =
          (IdxArrayLength === 1
            ? fileDataTotalValue().checkItemsSize + Size
            : sizeSum + Size + fileDataTotalValue().checkItemsSize) <= limitSize

        if (LimitSizeCondition) {
          updatedFileInfos[num] = {
            ...updatedFileInfos[num],
            check: true,
          }
          sizeSum += Size
          // shift idx 가 2개 이상할 떄 마지막 인덱스에서 값 초기화
          IdxArrayLength !== 1 && idx === IdxArrayLength - 1 && setShiftKeyFirstIdx(undefined)
        } else {
          sizeExceedCheckHandler()
          setShiftKeyFirstIdx(undefined)
        }
      })
    } else {
      const { check, Size } = updatedFileInfos[index]
      switch (true) {
        case check:
          updatedFileInfos[index] = {
            ...updatedFileInfos[index],
            check: false,
          }
          break
        case checkItemsSize + Size <= limitSize:
          updatedFileInfos[index] = {
            ...updatedFileInfos[index],
            check: true,
          }
          break
        default:
          sizeExceedCheckHandler()
          break
      }
      setShiftKeyFirstIdx(undefined)
    }
    dispatch(setExplorerFileInfos(updatedFileInfos))
  }

  const sizeExceedCheckHandler = () => {
    dispatch(
      setModalItems({
        type: 'alert',
        text: t('MODAL.EXPLORER_MAXSIZE_DOWN_CAPACITY', {
          defaultValue: 'Back up files up to 300MB at a time.',
        }) as string,
      }),
    )
  }

  //체크박스 상태관리 (all)
  const allCheckboxChange = () => {
    const updatedFileInfos = [...FileInfos]
    const allChecked = updatedFileInfos.every((fileInfo) => fileInfo.check)
    let size = !allChecked ? fileDataTotalValue().checkItemsSize : 0
    if (allChecked) {
      updatedFileInfos.forEach((_fileInfo, index: number) => {
        updatedFileInfos[index] = {
          ...updatedFileInfos[index],
          check: false,
        }
      })
    } else {
      updatedFileInfos.forEach((fileInfo, index: number) => {
        if (!fileInfo.check) {
          if (size + fileInfo.Size < limitSize) {
            size += fileInfo.Size
            updatedFileInfos[index] = {
              ...updatedFileInfos[index],
              check: !allChecked,
            }
          } else {
            dispatch(
              setModalItems({
                type: 'alert',
                text: t('MODAL.EXPLORER_MAXSIZE_DOWN_CAPACITY', {
                  defaultValue: 'Back up files up to 300MB at a time.',
                }) as string,
              }),
            )
          }
        } else return
      })
    }
    dispatch(setExplorerFileInfos(updatedFileInfos))
  }
  //파일 다운로드 요청
  const backUpRequestHandler = () => {
    const updatedFileInfos = [...FileInfos]
    const CheckItemsArray = FileInfos.filter((item: FileInfosType) => item.check).map(
      (item: FileInfosType) => item.Idx,
    )
    dispatch(SocketSender(Cmdtypes.EXPLORER_FILE_DOWNLOAD_REQUEST, { Idx: CheckItemsArray }))
    dispatch(setModalItems({ type: 'backupData', text: 'loading...' }))

    const updatedAllFileInfos = updatedFileInfos.map((fileInfo) => ({
      ...fileInfo,
      check: false,
    }))

    dispatch(setExplorerFileInfos(updatedAllFileInfos))
    setBackupBtnDisabled(true)
  }

  // download, internal, external 에 노출되는 경로 핸들링
  const directoryPathHandler = () => {
    const originFilterPath = `${Path?.replace(DownloadsDefaultPath, '/download')
      ?.replace(externalDefaultPath, '/')
      ?.replace(InternalDefaultPath, '/')}`

    let isDepthFilterPath = Path?.replace(DownloadsDefaultPath, '/down..')
      ?.replace(externalDefaultPath, '/')
      ?.replace(InternalDefaultPath, '/')
      .split('/') ?? ['']

    const returnDataForm = (str: string) => {
      return `$\{${str}}`
    }

    switch (true) {
      // 루트일떄
      case Path === InternalDefaultPath:
      case Path === externalDefaultPath:
      case Path === DownloadsDefaultPath:
        return returnDataForm(originFilterPath)
      default:
        isDepthFilterPath.shift()
        const depthLength = isDepthFilterPath.length
        let tempLastValue
        let tempLastSecondValue
        // path length
        switch (isDepthFilterPath?.length) {
          case 2:
            if (Number(isDepthFilterPath[depthLength -1]?.length) > 10) {
              tempLastValue = `${isDepthFilterPath[depthLength -1]?.substring(0, 10)}..`
            } else {
              tempLastValue = isDepthFilterPath[depthLength -1]
            }
            return returnDataForm(`${isDepthFilterPath[0]}/${tempLastValue}`)
          case 3:
            if (Number(isDepthFilterPath[depthLength -1]?.length) > 10) {
              tempLastValue = `${isDepthFilterPath[depthLength -1]?.substring(0, 10)}..`
            } else {
              tempLastValue = isDepthFilterPath[depthLength -1]
            }
            if (Number(isDepthFilterPath[depthLength -2]?.length) > 4) {
              tempLastSecondValue = `${isDepthFilterPath[depthLength -2]?.substring(0, 4)}..`
            } else {
              tempLastSecondValue = isDepthFilterPath[depthLength -2]
            }
            return returnDataForm(
              `${isDepthFilterPath[0]}/${tempLastSecondValue}/${tempLastValue}`,
            )
          default:
            if (Number(isDepthFilterPath[depthLength -1]?.length) > 10) {
              tempLastValue = `${isDepthFilterPath[depthLength -1]?.substring(0, 10)}..`
            } else {
              tempLastValue = isDepthFilterPath[depthLength -1]
            }
            if (Number(isDepthFilterPath[depthLength -2]?.length) > 4) {
              tempLastSecondValue = `${isDepthFilterPath[depthLength -2]?.substring(0, 4)}..`
            } else {
              tempLastSecondValue = isDepthFilterPath[depthLength -2]
            }
            return returnDataForm(`../${tempLastSecondValue}/${tempLastValue}`)
        }
    }
  }

  const selectedFileInfoHandler = () => {
    switch (true) {
      case arrayCheckList?.length !== 0:
        return `${arrayCheckList?.length} selected (${fileSizeHandler(
          fileDataTotalValue().checkItemsSize,
        )} / 300MB)`
      default:
        return ''
    }
  }

  useExplorerDetailEventBus()

  useEffect(() => {
    return () => {
      fileListResetDispatch()
    }
  }, [fileListResetDispatch])

  useEffect(() => {
    if (!deviceAuth.File || !Connection || !Title) {
      navigate('/explorer')
    }
  }, [deviceAuth.File, Connection, Title, navigate])

  useEffect(() => {
    if (backupBtnDisabled) {
      setTimeout(() => {
        setBackupBtnDisabled(false)
      }, 3000)
    }
  }, [backupBtnDisabled])

  const data = {
    ExtensionSvgHandler,
    fileDataTotalValue: fileDataTotalValue(),
    ResultPageList,
    allCheckboxChange,
    FolderInnerDispatch,
    curPage,
    setCurPage,
    paginationHandler,
    backUpRequestHandler,
    fileSizeHandler,
    allCheckValid,
    arrayCheckList,
    backupBtnDisabled,
    selectedFileInfoHandler,
    directoryPathHandler,
    eachCheckboxHandler,
  }

  return <ExplorerDetailTemplate data={data} />
}
export default ExplorerDetailPage
